import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { Download, UserPlus, Award, BarChart3, Search } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import Header from "./Header";
import AffiliateTable from "./AffiliateTable";
import AffiliateDetailModal from "./AffiliateDetailModal";
import AddUserModal from "./admin/AddUserModal";
import SummaryCards from "./SummaryCards";
import DateRangePicker from "./DateRangePicker";
import Button from "./ui/Button";
import Alert from "./ui/Alert";

// Use the new consistent data hook
import useAffiliateData from "../hooks/useAffiliateData";
import useWhitelistManager from "../hooks/useWhitelistManager";
import {
  sortData,
  getDefaultDateFilterState,
  isPastDaysRange,
} from "../utils/helpers";
import { useAuth } from "../context/AuthContext";
import { BRANDS } from "../utils/constants";
import {
  COMMISSION_TIERS,
  calculateTieredCommission,
} from "../utils/commissionTiers";
import { brandService } from "../lib/brandService";
import {
  getSystemDailyPerformance,
  getSystemAllTimePerformance,
  getSystemDailyPerformanceByDateRange,
} from "../lib/systemPerformanceService";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use the new consistent data hook
  const {
    getAllAffiliateData,
    getAvailableBrands,
    getAvailableDates,
    getIndividualAffiliateData,
    isLoading,
    error,
  } = useAffiliateData();

  // Use whitelist manager for individual calculations
  const { rebuildEarningSummary } = useWhitelistManager();

  // State management
  const [supabaseData, setSupabaseData] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [supabaseError, setSupabaseError] = useState(null);
  const [supabaseSuccess, setSupabaseSuccess] = useState(null);
  const [isLoadingAffiliateDetails, setIsLoadingAffiliateDetails] =
    useState(false);

  // Individual calculation state
  const [calculatingAffiliates, setCalculatingAffiliates] = useState({});

  // Filter states - use utility function for consistent default date range
  const [brand, setBrand] = useState(BRANDS[0]?.value || "holistic_hercules");
  const defaultDateFilter = getDefaultDateFilterState(7);
  const [dateFilterType, setDateFilterType] = useState(
    defaultDateFilter.dateFilterType
  );
  const [singleDate, setSingleDate] = useState(defaultDateFilter.singleDate);
  const [dateRangeStart, setDateRangeStart] = useState(
    defaultDateFilter.dateRangeStart
  );
  const [dateRangeEnd, setDateRangeEnd] = useState(
    defaultDateFilter.dateRangeEnd
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [commissionTiers, setCommissionTiers] = useState(COMMISSION_TIERS);
  const [commissionTiersLoading, setCommissionTiersLoading] = useState(false);
  const [commissionTiersError, setCommissionTiersError] = useState(null);

  // Sort configuration
  const [sortConfig, setSortConfig] = useState({
    key: "totalAdSpend",
    direction: "desc",
  });

  useEffect(() => {
    let isMounted = true;

    const loadCommissionTiers = async () => {
      if (!brand) {
        if (isMounted) {
          setCommissionTiers(COMMISSION_TIERS);
          setCommissionTiersError(null);
        }
        return;
      }

      setCommissionTiersLoading(true);
      setCommissionTiersError(null);

      try {
        const result = await brandService.getCommissionTiers(brand, {
          includeFuture: false,
        });
        if (!isMounted) {
          return;
        }

        if (
          result.success &&
          Array.isArray(result.data) &&
          result.data.length > 0
        ) {
          const mapped = result.data
            .map((row) => ({
              minROAS: Number(row.tier_min_roas),
              maxROAS:
                row.tier_max_roas !== null ? Number(row.tier_max_roas) : null,
              percentage: Number(row.commission_percentage),
              label:
                row.tier_label ||
                `${(Number(row.commission_percentage) * 100).toFixed(1)}%`,
            }))
            .filter(
              (tier) => !Number.isNaN(tier.minROAS) && tier.minROAS >= 0.8
            )
            .sort((a, b) => a.minROAS - b.minROAS);

          setCommissionTiers(mapped.length > 0 ? mapped : COMMISSION_TIERS);
        } else {
          setCommissionTiers(COMMISSION_TIERS);
          if (result.error) {
            setCommissionTiersError(result.error);
          }
        }
      } catch (err) {
        if (isMounted) {
          setCommissionTiers(COMMISSION_TIERS);
          setCommissionTiersError(
            err.message || "Failed to load commission tiers"
          );
        }
      } finally {
        if (isMounted) {
          setCommissionTiersLoading(false);
        }
      }
    };

    loadCommissionTiers();

    return () => {
      isMounted = false;
    };
  }, [brand]);

  const activeCommissionTiers = useMemo(
    () =>
      Array.isArray(commissionTiers) && commissionTiers.length > 0
        ? commissionTiers
        : COMMISSION_TIERS,
    [commissionTiers]
  );

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Chart configuration states - removed chartMetric since we show all totals

  // Build date filter object
  const buildDateFilter = useCallback(() => {
    return {
      type: dateFilterType,
      singleDate: dateFilterType === "single" ? singleDate : "",
      dateRangeStart: dateFilterType === "range" ? dateRangeStart : "",
      dateRangeEnd: dateFilterType === "range" ? dateRangeEnd : "",
    };
  }, [dateFilterType, singleDate, dateRangeStart, dateRangeEnd]);

  // Load data when filters change
  useEffect(() => {
    const loadData = async () => {
      setIsDataLoading(true);
      try {
        const dateFilter = buildDateFilter();

        // For affiliate data, exclude today if endDate is today
        let affiliateDateFilter = dateFilter;
        if (dateFilter.type === "range" && dateFilter.dateRangeEnd) {
          const today = new Date();
          const todayStr = today.toISOString().split("T")[0];

          if (dateFilter.dateRangeEnd === todayStr) {
            // Exclude today for affiliate data
            const endDateObj = new Date(dateFilter.dateRangeEnd);
            endDateObj.setDate(endDateObj.getDate() - 1);
            const modifiedEndDate = endDateObj.toISOString().split("T")[0];

            affiliateDateFilter = {
              ...dateFilter,
              dateRangeEnd: modifiedEndDate,
            };
          }
        }

        // Load affiliate data and dates in parallel
        const [affiliateResult, dates] = await Promise.all([
          getAllAffiliateData(brand, affiliateDateFilter),
          getAvailableDates(),
        ]);

        if (affiliateResult?.affiliates) {
          setSupabaseData(affiliateResult.affiliates);
        } else {
          setSupabaseData([]);
        }

        setAvailableDates(dates);

        // Clear any previous errors
        setSupabaseError(null);
      } catch (err) {
        console.error("❌ Dashboard: Error loading data:", err);
        setSupabaseError(err.message);
      }
      setIsDataLoading(false);
    };

    loadData();
  }, [getAllAffiliateData, getAvailableDates, brand, buildDateFilter]);

  // Handle API errors
  useEffect(() => {
    if (error) {
      setSupabaseError(error);
    }
  }, [error]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Filtered and sorted data for table
  const allFilteredData = useMemo(() => {
    if (!supabaseData) return [];

    // First filter by search query
    let filteredData = supabaseData;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredData = supabaseData.filter((affiliate) =>
        affiliate.name.toLowerCase().includes(query)
      );
    }

    // Then sort the filtered data
    return sortData([...filteredData], sortConfig);
  }, [supabaseData, sortConfig, searchQuery]);

  // Paginated data for display
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allFilteredData.slice(startIndex, endIndex);
  }, [allFilteredData, currentPage, itemsPerPage]);

  // Pagination calculations
  const totalPages = Math.ceil(allFilteredData.length / itemsPerPage);
  const totalItems = allFilteredData.length;

  // Summary stats for header cards - calculate from the same affiliate data shown in table
  const summaryStats = useMemo(() => {
    if (!supabaseData || supabaseData.length === 0) {
      return {
        totalAdSpend: 0,
        totalCommission: 0,
        totalRevenue: 0,
        avgROAS: 0,
      };
    }

    // Calculate totals from the same affiliate data displayed in the table
    const totals = supabaseData.reduce(
      (acc, affiliate) => ({
        totalAdSpend: acc.totalAdSpend + (affiliate.totalAdSpend || 0),
        totalCommission: acc.totalCommission + (affiliate.totalCommission || 0),
        totalRevenue: acc.totalRevenue + (affiliate.totalRevenue || 0),
      }),
      {
        totalAdSpend: 0,
        totalCommission: 0,
        totalRevenue: 0,
      }
    );

    return {
      ...totals,
      avgROAS:
        totals.totalAdSpend > 0 ? totals.totalRevenue / totals.totalAdSpend : 0,
    };
  }, [supabaseData]);

  // Calculate commission tier breakdown across all affiliates
  const commissionTierBreakdown = useMemo(() => {
    if (!supabaseData || supabaseData.length === 0) {
      return {
        tierBreakdown: [],
        summary: {
          totalAds: 0,
          qualifyingAds: 0,
          averageCommissionRate: 0,
          totalEarnings: 0,
          totalSpend: 0,
        },
      };
    }

    // Initialize tier tracking
    const tierMap = new Map();
    let totalEarnings = 0;
    let totalSpend = 0;
    let totalAds = 0;
    let qualifyingAffiliates = 0;

    // Initialize all tiers
    activeCommissionTiers.forEach((tier) => {
      tierMap.set(tier.label, {
        ...tier,
        adCount: 0,
        totalSpend: 0,
        totalEarnings: 0,
        roasRange: "",
        affiliateCount: 0,
      });
    });

    // Process each affiliate using aggregated data
    supabaseData.forEach((affiliate, affiliateIndex) => {
      const spend = affiliate.totalAdSpend || 0;
      const revenue = affiliate.totalRevenue || 0;
      const commission = affiliate.totalCommission || 0;
      const adCount = affiliate.adCount || 0;
      const avgROAS = affiliate.avgROAS || 0;

      // Skip affiliates with no spend
      if (spend <= 0) {
        return;
      }

      totalAds += adCount;
      totalSpend += spend;

      // Calculate overall ROAS for this affiliate
      const affiliateROAS = revenue / spend;

      // Only include affiliates with qualifying ROAS (>= 0.8)
      if (affiliateROAS >= 0.8) {
        qualifyingAffiliates++;
        totalEarnings += commission;

        // Find the tier for this affiliate based on their overall ROAS
        const commissionResult = calculateTieredCommission(
          affiliateROAS,
          spend,
          activeCommissionTiers
        );

        if (commissionResult.tier) {
          const tierData = tierMap.get(commissionResult.tier.label);
          if (tierData) {
            tierData.adCount += adCount; // Add all ads from this affiliate
            tierData.totalSpend += spend;
            tierData.totalEarnings += commission;
            tierData.affiliateCount++; // Count unique affiliates in this tier
          }
        }
      }
    });

    // Set ROAS ranges for display
    const tiers = Array.from(tierMap.values());
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const nextTier = tiers[i + 1];
      if (nextTier) {
        tier.roasRange = `${tier.minROAS.toFixed(1)} - ${nextTier.minROAS.toFixed(1)}`;
      } else {
        tier.roasRange = `${tier.minROAS.toFixed(1)} - ∞`;
      }
    }

    // Show all tiers and sort by minROAS (removed filter to display all tiers, even empty ones)
    const activeTiers = tiers.sort((a, b) => a.minROAS - b.minROAS);

    const result = {
      tierBreakdown: activeTiers,
      summary: {
        totalAds,
        qualifyingAds: qualifyingAffiliates, // Changed to qualifyingAffiliates for clarity
        averageCommissionRate:
          totalSpend > 0 ? (totalEarnings / totalSpend) * 100 : 0,
        totalEarnings,
        totalSpend,
      },
    };

    return result;
  }, [activeCommissionTiers, supabaseData]);

  // Chart configuration states - simplified
  const [chartMetric, setChartMetric] = useState("spend");

  // State for daily performance data
  const [dailyPerformanceData, setDailyPerformanceData] = useState([]);
  const [chartGroupBy, setChartGroupBy] = useState("daily");

  // Chart loading states
  const [isChartLoading, setIsChartLoading] = useState(false);

  // Load REAL system-wide daily performance data from database
  useEffect(() => {
    const loadSystemDailyData = async () => {
      try {
        console.log("🚀 Loading REAL system daily performance data...", {
          dateFilterType,
          dateRangeStart,
          dateRangeEnd,
          singleDate,
          brand,
        });

        // Clear chart data immediately when date range changes
        setDailyPerformanceData([]);
        setIsChartLoading(false);

        let dailyData = [];

        let dataPromise;

        if (dateFilterType === "all") {
          // Use All Time function for unlimited data with simple progress tracking
          setIsChartLoading(true);

          // No timeout for All Time - let it complete fully
          dailyData = await getSystemAllTimePerformance(brand);
        } else if (
          dateFilterType === "range" &&
          dateRangeStart &&
          dateRangeEnd
        ) {
          // Calculate days to check if we should use All Time for very large ranges
          const startDate = new Date(dateRangeStart);
          const endDate = new Date(dateRangeEnd);
          const timeDiff = endDate.getTime() - startDate.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates

          // Limit to prevent API timeouts - use All Time for large ranges
          if (daysDiff > 60) {
            setIsChartLoading(true);

            // No timeout for large ranges - use date range filtering
            dailyData = await getSystemAllTimePerformance(
              brand,
              null,
              dateRangeStart,
              dateRangeEnd
            );
          } else {
            // Use actual date range to avoid timezone issues with timeout for smaller ranges
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error("Request timeout after 30 seconds")),
                30000
              )
            );

            dataPromise = getSystemDailyPerformanceByDateRange(
              dateRangeStart,
              dateRangeEnd,
              brand
            );

            const guardedDataPromise = dataPromise.catch(() => null);
            dailyData = await Promise.race([
              guardedDataPromise,
              timeoutPromise,
            ]);
          }
        } else if (dateFilterType === "single") {
          // For single date, use the actual selected date to avoid timezone issues
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Request timeout after 30 seconds")),
              30000
            )
          );

          dataPromise = getSystemDailyPerformanceByDateRange(
            singleDate,
            singleDate,
            brand
          );

          const guardedDataPromise = dataPromise.catch(() => null);
          dailyData = await Promise.race([guardedDataPromise, timeoutPromise]);
        } else {
          // Fallback to 7 days
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Request timeout after 30 seconds")),
              30000
            )
          );

          dataPromise = getSystemDailyPerformance(7, brand);
          const guardedDataPromise = dataPromise.catch(() => null);
          dailyData = await Promise.race([guardedDataPromise, timeoutPromise]);
        }

        // Update chart data immediately since we're using single API calls
        setDailyPerformanceData(dailyData);
        setIsChartLoading(false);

        console.log(
          "✅ REAL system daily performance data loaded:",
          dailyData.length,
          "days"
        );
      } catch (err) {
        console.error("❌ Error loading system daily performance:", err);

        setDailyPerformanceData([]);
        setIsChartLoading(false);
      }
    };

    loadSystemDailyData();
  }, [dateFilterType, dateRangeStart, dateRangeEnd, singleDate, brand]); // Include all date filter dependencies

  // Process chart data with configurable grouping
  const processedChartData = useMemo(() => {
    if (!dailyPerformanceData.length) return [];

    let groupedData = new Map();

    dailyPerformanceData.forEach((day) => {
      // Parse date in local timezone to avoid UTC conversion issues
      const [year, month, dayNum] = day.date.split("-").map(Number);
      const date = new Date(year, month - 1, dayNum);

      // Determine grouping based on chartGroupBy setting
      let groupKey, groupLabel, fullLabel;

      if (chartGroupBy === "daily") {
        groupKey = day.date;
        groupLabel = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        fullLabel = date.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } else if (chartGroupBy === "weekly") {
        // Get Monday of the week
        const monday = new Date(date);
        const dayOfWeek = date.getDay();
        const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        monday.setDate(diff);
        groupKey = monday.toISOString().split("T")[0];
        groupLabel = `Week ${monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
        fullLabel = `Week of ${monday.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
        })}`;
      } else if (chartGroupBy === "monthly") {
        groupKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        groupLabel = date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
        fullLabel = date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      }

      if (!groupedData.has(groupKey)) {
        groupedData.set(groupKey, {
          period: groupKey,
          label: groupLabel,
          fullLabel: fullLabel,
          spend: 0,
          revenue: 0,
          purchases: 0,
          commission: 0,
          adCount: 0,
          dayCount: 0,
          roas: 0,
          value: 0,
        });
      }

      const group = groupedData.get(groupKey);
      group.spend += day.spend;
      group.revenue += day.revenue;
      group.purchases += day.purchases;
      group.commission += day.commission;
      group.adCount += day.adCount;
      group.dayCount += 1;
    });

    // Calculate ROAS and set value based on selected metric
    const result = Array.from(groupedData.values()).map((group) => {
      group.roas = group.spend > 0 ? group.revenue / group.spend : 0;
      group.value = group[chartMetric] || 0;
      return group;
    });

    const sortedResult = result.sort((a, b) =>
      a.period.localeCompare(b.period)
    );

    return sortedResult;
  }, [dailyPerformanceData, chartMetric, chartGroupBy]);

  // Get the current filter text (same logic as DateRangePicker)
  const getFilterText = useCallback(() => {
    if (dateFilterType === "all") return "All Time";

    if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
      // Check for quick ranges using consistent utility
      if (isPastDaysRange(dateRangeStart, dateRangeEnd, 7)) {
        return "Past 7 Days";
      }
      if (isPastDaysRange(dateRangeStart, dateRangeEnd, 30)) {
        return "Past 30 Days";
      }
      if (isPastDaysRange(dateRangeStart, dateRangeEnd, 90)) {
        return "Past 90 Days";
      }
    }

    if (dateFilterType === "single") {
      const date = parseLocalDate(singleDate);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }

    return "Custom Range";
  }, [dateFilterType, singleDate, dateRangeStart, dateRangeEnd]);

  // Event handlers
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
    // Reset to first page when sorting changes
    setCurrentPage(1);
  }, []);

  // Pagination functions
  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  const handleAddUser = useCallback(() => setShowAddUserModal(true), []);

  const clearSupabaseMessages = useCallback(() => {
    setSupabaseError(null);
    setSupabaseSuccess(null);
  }, []);

  const exportResults = useCallback(() => {
    if (!allFilteredData) return;
    const exportData = allFilteredData.map(
      ({
        name,
        adCount,
        totalAdSpend,
        avgROAS,
        totalPurchases,
        totalCommission,
      }) => ({
        "Affiliate Name": name,
        "Ad Count": adCount,
        "Total Ad Spend": totalAdSpend.toFixed(2),
        ROAS: avgROAS ? avgROAS.toFixed(2) : "0.00",
        "Total Purchases": totalPurchases || 0,
        Commission: totalCommission.toFixed(2),
      })
    );
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `affiliate-performance-${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [allFilteredData]);

  // Individual affiliate calculation
  const handleCalculateAffiliate = useCallback(
    async (affiliateName) => {
      try {
        setCalculatingAffiliates((prev) => ({
          ...prev,
          [affiliateName]: true,
        }));
        setSupabaseError(null);
        setSupabaseSuccess(null);

        await rebuildEarningSummary(affiliateName);

        setSupabaseSuccess(
          `✅ Successfully calculated commission for ${affiliateName}`
        );

        // Force a complete data refresh to show updated results
        const dateFilter = buildDateFilter();
        const affiliateResult = await getAllAffiliateData(brand, dateFilter);

        if (affiliateResult.affiliates) {
          const updatedAffiliate = affiliateResult.affiliates.find(
            (a) => a.name === affiliateName
          );
          setSupabaseData(affiliateResult.affiliates);
        } else {
          console.warn("⚠️ No affiliate data received after calculation");
        }
      } catch (error) {
        console.error(`❌ Error calculating ${affiliateName}:`, error);
        setSupabaseError(
          `Failed to calculate commission for ${affiliateName}: ${error.message}`
        );
      } finally {
        setCalculatingAffiliates((prev) => ({
          ...prev,
          [affiliateName]: false,
        }));
      }
    },
    [rebuildEarningSummary, buildDateFilter, getAllAffiliateData, brand]
  );

  // Handle affiliate detail modal
  const handleViewAffiliateDetails = useCallback(
    async (affiliate) => {
      try {
        setSupabaseError(null);

        // Show modal immediately with affiliate data (no individual ads yet)
        const affiliateWithEmptyAds = {
          ...affiliate,
          ads: [], // Start with empty ads array
        };

        setSelectedAffiliate(affiliateWithEmptyAds);
        setIsLoadingAffiliateDetails(true);

        // Fetch individual ad data for this affiliate with current filters
        const dateFilter = buildDateFilter();
        const individualAds = await getIndividualAffiliateData(
          affiliate.name,
          brand,
          dateFilter
        );

        // Update the affiliate object with the fetched individual ad data
        const affiliateWithAds = {
          ...affiliate,
          ads: individualAds,
        };

        setSelectedAffiliate(affiliateWithAds);
      } catch (error) {
        console.error(
          `❌ Error loading affiliate details for ${affiliate.name}:`,
          error
        );
        setSupabaseError(
          `Failed to load details for ${affiliate.name}: ${error.message}`
        );
      } finally {
        setIsLoadingAffiliateDetails(false);
      }
    },
    [getIndividualAffiliateData, brand, buildDateFilter]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Top Control Row */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 w-full lg:w-auto">
              <DateRangePicker
                dateFilterType={dateFilterType}
                setDateFilterType={setDateFilterType}
                singleDate={singleDate}
                setSingleDate={setSingleDate}
                dateRangeStart={dateRangeStart}
                setDateRangeStart={setDateRangeStart}
                dateRangeEnd={dateRangeEnd}
                setDateRangeEnd={setDateRangeEnd}
                availableDates={availableDates}
              />
              <div className="flex items-center gap-2 w-full lg:w-auto">
                <div className="relative group">
                  <select
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className="appearance-none bg-gray-800/50 border border-gray-700 text-white rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all cursor-pointer text-sm font-medium"
                  >
                    {BRANDS.map((brand) => (
                      <option
                        key={brand.value}
                        value={brand.value}
                        className="bg-gray-800"
                      >
                        {brand.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 hidden sm:flex items-center pr-2 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2 sm:gap-3 lg:hidden">
                  <Button
                    onClick={handleAddUser}
                    className="bg-gray-800/60 hover:bg-gray-700/80 border border-gray-700 text-gray-200 font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2 whitespace-nowrap min-w-fit"
                  >
                    <UserPlus size={16} /> Add User
                  </Button>
                  <Button
                    onClick={exportResults}
                    className="bg-gray-200 hover:bg-gray-300 text-black font-semibold rounded-lg px-3 py-2 text-[0.6rem] sm:text-sm flex items-center gap-2"
                  >
                    <Download size={16} /> Export
                  </Button>
                </div>
              </div>
            </div>

            <div className="order-last lg:order-none flex-grow flex justify-center w-full">
              <SummaryCards summaryStats={summaryStats} />
            </div>

            {/* Right actions (desktop only) */}
            <div className="hidden lg:flex items-center gap-3">
              <Button
                onClick={handleAddUser}
                className="bg-gray-800/60 hover:bg-gray-700/80 border border-gray-700 text-gray-200 font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2 whitespace-nowrap min-w-fit"
              >
                <UserPlus size={16} /> Add User
              </Button>
              <Button
                onClick={exportResults}
                className="bg-gray-200 hover:bg-gray-300 text-black font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2"
              >
                <Download size={16} /> Export
              </Button>
            </div>
          </div>

          {/* Alerts */}
          {supabaseError && (
            <Alert
              type="error"
              message={supabaseError}
              onClose={clearSupabaseMessages}
            />
          )}
          {supabaseSuccess && (
            <Alert
              type="success"
              message={supabaseSuccess}
              onClose={clearSupabaseMessages}
            />
          )}

          {/* Show helpful message if all affiliates have 0 values */}
          {!isDataLoading &&
            supabaseData &&
            supabaseData.length > 0 &&
            supabaseData.every(
              (affiliate) =>
                affiliate.totalAdSpend === 0 && affiliate.totalCommission === 0
            ) && (
              <Alert
                type="warning"
                message="No data for the selected brand and date range — every affiliate shows $0.00. This usually just means that brand had no uploads in these dates; try a different brand or a wider date range. If you're sure data should appear here, an admin can rebuild via Settings > Whitelist Management > 'Update All Summaries'."
                onClose={() => {}}
              />
            )}

          {/* Performance Charts */}
          {!isDataLoading && supabaseData && supabaseData.length > 0 && (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-xl">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <BarChart3 className="h-6 w-6 text-[#B8460E]" />
                    Performance Analytics
                  </h3>
                  <p className="text-sm text-gray-400">
                    Overview of affiliate performance and top performers
                  </p>
                </div>

                {/* Chart Controls - metric selector and group by */}
                <div className="flex items-center gap-2 flex-nowrap overflow-hidden whitespace-nowrap lg:ml-auto">
                  {/* Group By Selector */}
                  <div className="flex items-center flex-shrink-0">
                    <select
                      value={chartGroupBy}
                      onChange={(e) => setChartGroupBy(e.target.value)}
                      className="bg-gray-800/50 border border-gray-700 text-white rounded-md px-2 py-1 text-xs sm:text-sm sm:px-3 sm:py-1.5 leading-tight focus:outline-none focus:ring-2 focus:ring-orange-500"
                      aria-label="Group By"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {/* Metric Selector */}
                  <div className="flex items-center flex-shrink-0">
                    <select
                      value={chartMetric}
                      onChange={(e) => setChartMetric(e.target.value)}
                      className="bg-gray-800/50 border border-gray-700 text-white rounded-md px-2 py-1 text-xs sm:text-sm sm:px-3 sm:py-1.5 leading-tight focus:outline-none focus:ring-2 focus:ring-orange-500"
                      aria-label="Metric"
                    >
                      <option value="spend">Ad Spend</option>
                      <option value="revenue">Revenue</option>
                      <option value="purchases">Purchases</option>
                      <option value="commission">Commission</option>
                      <option value="roas">Avg ROAS</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Main Bar Chart - Full Width */}
              <div className="bg-white/5 rounded-xl p-2 sm:p-4 border border-white/10">
                <h4 className="text-lg font-semibold text-white mb-2 sm:mb-4">
                  System Performance - {getFilterText()}
                  {isChartLoading && (
                    <span className="ml-2 text-sm text-gray-400">
                      Loading data...
                    </span>
                  )}
                </h4>
                <div className="h-[30rem] sm:h-[28rem] relative">
                  {isChartLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm rounded-lg">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
                        <p className="text-white text-sm">
                          Loading chart data...
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={processedChartData}
                      margin={{ top: 40, right: 0, left: 0, bottom: 20 }}
                      barSize={30}
                    >
                      <defs>
                        <linearGradient
                          id="dashboardBarGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#FF6B35"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#B8460E"
                            stopOpacity={0.8}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(255,255,255,0.1)"
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                        tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                        tick={{ fontSize: 12, fill: "white" }}
                        angle={-35}
                        textAnchor="end"
                        height={36}
                      />
                      <YAxis
                        axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                        tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                        tick={{ fontSize: 12, fill: "white" }}
                        tickFormatter={(value) => {
                          if (chartMetric === "roas") return value.toFixed(1);
                          if (chartMetric === "purchases")
                            return Math.round(value);
                          if (chartMetric === "commission") {
                            return value > 1000
                              ? `$${(value / 1000).toFixed(1)}k`
                              : `$${Math.round(value)}`;
                          }
                          return value > 1000
                            ? `$${(value / 1000).toFixed(0)}k`
                            : `$${Math.round(value)}`;
                        }}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-black/80 backdrop-blur-2xl text-white text-sm rounded-xl px-4 py-3 border border-white/5 shadow-2xl">
                                <p className="font-semibold text-[#FF6B35] mb-2 text-center">
                                  {data.fullLabel}
                                </p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                  <div className="flex items-center">
                                    <div className="w-2 h-2 bg-green-400/60 rounded-full mr-2"></div>
                                    <span className="text-gray-300">
                                      Spend:
                                    </span>
                                  </div>
                                  <div className="text-green-300/80 font-medium text-right">
                                    ${data.spend?.toLocaleString() || "0"}
                                  </div>

                                  <div className="flex items-center">
                                    <div className="w-2 h-2 bg-blue-400/60 rounded-full mr-2"></div>
                                    <span className="text-gray-300">
                                      Revenue:
                                    </span>
                                  </div>
                                  <div className="text-blue-300/80 font-medium text-right">
                                    ${data.revenue?.toLocaleString() || "0"}
                                  </div>

                                  <div className="flex items-center">
                                    <div className="w-2 h-2 bg-orange-400/60 rounded-full mr-2"></div>
                                    <span className="text-gray-300">
                                      Commission:
                                    </span>
                                  </div>
                                  <div className="text-orange-300/80 font-medium text-right">
                                    ${data.commission?.toLocaleString() || "0"}
                                  </div>

                                  <div className="flex items-center">
                                    <div className="w-2 h-2 bg-purple-400/60 rounded-full mr-2"></div>
                                    <span className="text-gray-300">Avg ROAS:</span>
                                  </div>
                                  <div className="text-purple-300/80 font-medium text-right">
                                    {data.roas?.toFixed(2) || "0.00"}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend
                        verticalAlign="top"
                        align="center"
                        height={24}
                        wrapperStyle={{ color: "white" }}
                        formatter={(value, entry) => (
                          <span
                            style={{
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            {chartMetric === "spend"
                              ? "Ad Spend"
                              : chartMetric === "revenue"
                                ? "Revenue"
                                : chartMetric === "purchases"
                                  ? "Purchases"
                                  : chartMetric === "commission"
                                    ? "Commission"
                                    : "Avg ROAS"}
                          </span>
                        )}
                      />
                      <Bar
                        dataKey="value"
                        fill="url(#dashboardBarGradient)"
                        radius={[4, 4, 0, 0]}
                        style={{
                          filter:
                            "drop-shadow(0 0 8px rgba(255, 107, 53, 0.3))",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(data, index) => {
                          // Add glass hover effect
                          const bars = document.querySelectorAll(
                            ".recharts-bar-rectangle path"
                          );
                          if (bars[index]) {
                            bars[index].style.filter =
                              "drop-shadow(0 0 12px rgba(255, 107, 53, 0.6)) brightness(1.1)";
                            bars[index].style.stroke =
                              "rgba(255, 255, 255, 0.05)";
                            bars[index].style.strokeWidth = "1";
                          }
                        }}
                        onMouseLeave={(data, index) => {
                          // Remove glass hover effect
                          const bars = document.querySelectorAll(
                            ".recharts-bar-rectangle path"
                          );
                          if (bars[index]) {
                            bars[index].style.filter =
                              "drop-shadow(0 0 8px rgba(255, 107, 53, 0.3))";
                            bars[index].style.stroke = "none";
                            bars[index].style.strokeWidth = "0";
                          }
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Search Input */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Search className="h-5 w-5 text-[#B8460E]" />
                Search Affiliates
              </h3>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search affiliates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-gray-800/50 border border-gray-700 text-white rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all text-sm font-medium placeholder-gray-400 w-64"
                />
                {/* Clear search button */}
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content Area */}
          {isDataLoading ? (
            <div className="flex justify-center items-center h-96">
              <div className="w-16 h-16 border-4 border-white/20 border-t-[#B8460E] rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Search Results Indicator */}
              {searchQuery.trim() && (
                <div className="bg-white/5 backdrop-blur-xl rounded-lg border border-white/10 p-3 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Search className="h-4 w-4 text-[#B8460E]" />
                    <span className="text-gray-300">
                      Search results for "{searchQuery}":
                    </span>
                    <span className="text-white font-semibold">
                      {totalItems} of {supabaseData.length} affiliates
                    </span>
                    {totalItems === 0 && (
                      <span className="text-gray-400 ml-2">
                        - No matches found
                      </span>
                    )}
                  </div>
                </div>
              )}

              <AffiliateTable
                sortedData={paginatedData}
                onSort={handleSort}
                sortConfig={sortConfig}
                onViewDetails={handleViewAffiliateDetails}
                processedData={supabaseData}
                onCalculateAffiliate={handleCalculateAffiliate}
                isCalculating={calculatingAffiliates}
              />

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4">
                  {/* Page Info */}
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <span>
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                      {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                      {totalItems} affiliates
                    </span>
                  </div>

                  {/* Page Controls */}
                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2"
                    >
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {(() => {
                        const pages = [];
                        const maxVisiblePages = 5;
                        let startPage = Math.max(
                          1,
                          currentPage - Math.floor(maxVisiblePages / 2)
                        );
                        let endPage = Math.min(
                          totalPages,
                          startPage + maxVisiblePages - 1
                        );

                        // Adjust if we're near the end
                        if (endPage - startPage + 1 < maxVisiblePages) {
                          startPage = Math.max(
                            1,
                            endPage - maxVisiblePages + 1
                          );
                        }

                        // First page + ellipsis if needed
                        if (startPage > 1) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => handlePageChange(1)}
                              className="w-8 h-8 rounded-lg text-sm font-medium transition-colors hover:bg-white/10 text-white"
                            >
                              1
                            </button>
                          );
                          if (startPage > 2) {
                            pages.push(
                              <span
                                key="start-ellipsis"
                                className="text-gray-400 px-2"
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        // Visible page numbers
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                                i === currentPage
                                  ? "bg-[#B8460E] text-white shadow-lg"
                                  : "hover:bg-white/10 text-white"
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }

                        // Ellipsis + last page if needed
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="end-ellipsis"
                                className="text-gray-400 px-2"
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages}
                              onClick={() => handlePageChange(totalPages)}
                              className="w-8 h-8 rounded-lg text-sm font-medium transition-colors hover:bg-white/10 text-white"
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                    </div>

                    {/* Next Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {/* Commission Tier Analytics */}
              {(() => {
                return commissionTierBreakdown.tierBreakdown.length > 0;
              })() && (
                <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-6 backdrop-blur-xl">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Award className="h-6 w-6 text-[#B8460E]" />
                        Business Commission Analytics
                        <span className="text-xs font-normal text-gray-400">(Per-Ad ROAS)</span>
                      </h3>
                      <p className="text-sm text-gray-400">
                        Performance distribution across all affiliates by
                        commission tier. Each ad is evaluated individually.
                      </p>
                    </div>
                  </div>

                  {/* Commission Tier Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                    {commissionTierBreakdown.tierBreakdown.map(
                      (tier, index) => {
                        // Determine performance level based on ROAS
                        const getPerformanceLevel = (minROAS) => {
                          if (minROAS >= 2.5) return "top";
                          if (minROAS >= 1.5) return "medium";
                          return "low";
                        };

                        // Get color scheme based on performance level
                        const getPerformanceColors = (level) => {
                          switch (level) {
                            case "top":
                              return {
                                background:
                                  "bg-gradient-to-br from-orange-500/20 via-red-500/15 to-orange-600/20",
                                border: "border-orange-400/40",
                                hover:
                                  "hover:bg-gradient-to-br hover:from-orange-500/30 hover:via-red-500/25 hover:to-orange-600/30",
                                badge:
                                  "bg-gradient-to-r from-orange-500/30 to-red-500/30 text-orange-200 border border-orange-400/30",
                                glow: "shadow-lg shadow-orange-500/20",
                                shimmer:
                                  "bg-gradient-to-r from-transparent via-orange-300/20 to-transparent",
                                animatedBg:
                                  "bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-600/10",
                                earnedColor: "text-orange-300",
                              };
                            case "medium":
                              return {
                                background:
                                  "bg-gradient-to-br from-amber-500/10 via-yellow-500/8 to-amber-600/10",
                                border: "border-amber-400/25",
                                hover:
                                  "hover:bg-gradient-to-br hover:from-amber-500/15 hover:via-yellow-500/12 hover:to-amber-600/15",
                                badge:
                                  "bg-amber-500/20 text-amber-300 border border-amber-400/20",
                                glow: "shadow-md shadow-amber-500/10",
                                shimmer:
                                  "bg-gradient-to-r from-transparent via-amber-300/15 to-transparent",
                                animatedBg:
                                  "bg-gradient-to-r from-amber-500/8 via-yellow-500/6 to-amber-600/8",
                                earnedColor: "text-amber-300",
                              };
                            case "low":
                            default:
                              return {
                                background:
                                  "bg-gradient-to-br from-gray-500/5 via-slate-500/3 to-gray-600/5",
                                border: "border-gray-400/15",
                                hover:
                                  "hover:bg-gradient-to-br hover:from-gray-500/8 hover:via-slate-500/5 hover:to-gray-600/8",
                                badge:
                                  "bg-gray-500/15 text-gray-400 border border-gray-400/15",
                                glow: "shadow-sm shadow-gray-500/5",
                                shimmer:
                                  "bg-gradient-to-r from-transparent via-gray-300/10 to-transparent",
                                animatedBg:
                                  "bg-gradient-to-r from-gray-500/3 via-slate-500/2 to-gray-600/3",
                                earnedColor: "text-gray-400",
                              };
                          }
                        };

                        const performanceLevel = getPerformanceLevel(
                          tier.minROAS
                        );
                        const colors = getPerformanceColors(performanceLevel);

                        return (
                          <div
                            key={tier.label}
                            className={`${colors.background} border ${colors.border} rounded-lg p-4 backdrop-blur-xl ${colors.hover} transition-all duration-300 relative overflow-hidden ${colors.glow} group`}
                          >
                            {/* Shimmer effect */}
                            <div
                              className={`absolute inset-0 ${colors.shimmer} rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                              style={{
                                animation: `shimmer 3s ease-in-out infinite ${index * 0.5}s`,
                              }}
                            ></div>

                            {/* Tier badge */}
                            <div className="absolute top-3 right-3 z-10">
                              <div
                                className={`${colors.badge} px-2 py-1 rounded-full text-xs font-semibold backdrop-blur-sm`}
                              >
                                {tier.label}
                              </div>
                            </div>

                            {/* Tier header */}
                            <div className="mb-3 relative z-10">
                              <h4
                                className={`text-lg font-semibold mb-1 ${performanceLevel === "top" ? "text-orange-200" : performanceLevel === "medium" ? "text-amber-200" : "text-gray-300"}`}
                              >
                                {tier.label} Commission
                              </h4>
                              <p
                                className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}
                              >
                                ROAS {tier.roasRange}
                              </p>
                            </div>

                            {/* Tier stats */}
                            <div className="space-y-2 relative z-10">
                              <div className="flex justify-between">
                                <span
                                  className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}
                                >
                                  Ads:
                                </span>
                                <span
                                  className={`text-sm font-medium ${performanceLevel === "top" ? "text-orange-200" : performanceLevel === "medium" ? "text-amber-200" : "text-gray-400"}`}
                                >
                                  {tier.adCount}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span
                                  className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}
                                >
                                  Affiliates:
                                </span>
                                <span
                                  className={`text-sm font-medium ${performanceLevel === "top" ? "text-orange-200" : performanceLevel === "medium" ? "text-amber-200" : "text-gray-400"}`}
                                >
                                  {tier.affiliateCount}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span
                                  className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}
                                >
                                  Spend:
                                </span>
                                <span
                                  className={`text-sm font-medium ${performanceLevel === "top" ? "text-orange-200" : performanceLevel === "medium" ? "text-amber-200" : "text-gray-400"}`}
                                >
                                  $
                                  {tier.totalSpend.toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span
                                  className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}
                                >
                                  Earned:
                                </span>
                                <span
                                  className={`text-sm font-medium ${colors.earnedColor}`}
                                >
                                  $
                                  {tier.totalEarnings.toLocaleString(
                                    undefined,
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Animated background */}
                            <div
                              className={`absolute inset-0 ${colors.animatedBg} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg`}
                            ></div>
                          </div>
                        );
                      }
                    )}
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                      <div className="text-2xl font-bold text-white">
                        {commissionTierBreakdown.summary.qualifyingAds}
                      </div>
                      <div className="text-sm text-gray-400">
                        Qualifying Affiliates
                      </div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                      <div className="text-2xl font-bold text-white">
                        {commissionTierBreakdown.summary.averageCommissionRate.toFixed(
                          2
                        )}
                        %
                      </div>
                      <div className="text-sm text-gray-400">
                        Avg Commission Rate
                      </div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                      <div className="text-2xl font-bold text-green-400">
                        $
                        {commissionTierBreakdown.summary.totalEarnings.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        Total Earnings
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {selectedAffiliate && (
        <AffiliateDetailModal
          affiliate={selectedAffiliate}
          onClose={() => setSelectedAffiliate(null)}
          isLoading={isLoadingAffiliateDetails}
        />
      )}

      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onSuccess={(message) => {
            setSupabaseSuccess(message);
            setShowAddUserModal(false);
          }}
          onError={(message) => {
            setSupabaseError(message);
            setShowAddUserModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
